import { useEffect, useState } from "react";
import NoData from "../../../../../components/noData/noData";
import CommonTable from "../../../../../components/table/table";
import DefaultButton from "../../../../../components/buttons/defaultButton";
import { Icons } from "../../../../../../utils/icons";
import BottomDrawer from "../../../../common/drawer/bottomDrawer";
import CommonForm from "../../../../../components/form/form";
import {
  createEducation,
  deleteEducation,
  getEducationList,
  updateEducation,
} from "../../../../../../services/workspace/user.service";
import moment from "moment";
import useCommonFetch from "../../../../../../hooks/useCommonFetch";

const EducationCard = ({ staff, heandleFetchData }) => {
  const [isOpenSheet, setIsOpenSheet] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [educationData, setEducationData] = useState([]);
  const [loadingEducation, setLoadingEducation] = useState(true);

  // Prefer data from dedicated API; fall back to staff.educations if not yet loaded
  const educations =
    educationData && educationData.length > 0
      ? educationData
      : staff?.educations || [];

  const handleEdit = (item) => {
    // Existing image URL from table row
    const existingImageUrl = item.certificate?.data || item.image || "";

    // Pre-fill uploader with a pseudo file object so UI shows attachment properly
    const prefilledDocuments = existingImageUrl
      ? [
          {
            uuid: `education-cert-${item.id.data}`,
            name: existingImageUrl.split("/").pop() || "certificate.png",
            size: 0,
            type: "image/png",
            localpreview: existingImageUrl,
            loading: false,
            error: false,
            progress: 100,
            data: existingImageUrl,
          },
        ]
      : [];

    const initialData = {
      id: item.id.data,
      qualification: String(item.qualification.data ?? ""),
      degree: String(item.degree.data ?? ""),
      specialization: String(item.specialization.data ?? ""),
      university: String(item.university.data ?? ""),
      // Normalize to YYYY-MM-DD so date validation passes and DefaultDatePicker shows value
      year_of_passing: moment(
        item.year_of_passing?.data,
        "YYYY",
        true
      ).isValid()
        ? moment(item.year_of_passing.data, "YYYY").format("YYYY-MM-DD")
        : "",
      marks: String(item.marks.data ?? ""),
      marks_type: String(item.marks_type.data ?? ""),
      certificate: existingImageUrl,
      address: String(item.address?.data ?? ""),
      documents: prefilledDocuments,
    };
    setCurrentRecord(initialData);
    setIsEditMode(true);
    setIsOpenSheet(true);
  };

  const TableHeaderData = [
    { label: "Qualification", key: "qualification" },
    { label: "Degree / Program", key: "degree" },
    { label: "Specialization", key: "specialization" },
    { label: "Institute / University", key: "university" },
    { label: "Year of Passing", key: "year_of_passing" },
    { label: "Marks", key: "marks" },
    { label: "Marks type", key: "marks_type" },
    { label: "Certificate", key: "certificate" },
  ];

  const { handleCreate, handleUpdate, handleDelete } = useCommonFetch();

  // Initial fetch for education list using dedicated API
  useEffect(() => {
    if (!staff?.id) {
      setLoadingEducation(false);
      return;
    }
    setLoadingEducation(true);
    getEducationList({ user_id: staff.id })
      .then((list) => {
        setEducationData(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setEducationData([]);
      })
      .finally(() => setLoadingEducation(false));
  }, [staff?.id]);

  const tableData = educations.map((item) => ({
    qualification: { data: item.qualification, variant: "text" },
    degree: { data: item.degree, variant: "text" },
    specialization: { data: item.field_of_study, variant: "text" },
    university: { data: item.school, variant: "text" },
    year_of_passing: { data: item.passing_year, variant: "text" },
    marks: { data: item.marks, variant: "text" },
    marks_type: { data: item.marks_type, variant: "text" },
    // Show image preview for education certificate (similar to documentsCard)
    certificate: { data: item.image || item.certificate, variant: "image" },
    // keep address (location) hidden in table but available for edit drawer
    address: { data: item.location, variant: "text" },
    id: { data: item.uuid, variant: "text" }, // needed for actions
    actions: { data: item.uuid, variant: "text" }, // this triggers the action buttons
  }));

  const actionButton = {
  edit: (item) => handleEdit(item),
  delete: async (item) => {
    await handleDelete(item?.id?.data || item, deleteEducation, {
      successMessage: "Experience deleted successfully!",
      errorMessage: "Failed to delete experience!",
    });
    heandleFetchData();
    // Refresh local education list so table updates immediately
    try {
      const updatedList = await getEducationList({ user_id: staff.id });
      setEducationData(Array.isArray(updatedList) ? updatedList : []);
    } catch (e) {
      console.error("Failed to refresh education list after delete:", e);
    }
  },
};

  const userFormFields = [
    {
      name: "qualification",
      colSpan: 6,
      type: "text",
      label: "Qualification",
      placeholder: "Enter here",
      required: true,
    },
    {
      name: "degree",
      colSpan: 6,
      type: "text",
      label: "Degree/Program",
      placeholder: "Enter here",
      required: true,
    },
    {
      name: "specialization",
      colSpan: 6,
      type: "text",
      label: "Specialization",
      placeholder: "Enter here",
      required: true,
    },

    {
      name: "year_of_passing",
      colSpan: 6,
      type: "date",
      label: "Year of Passing",
      placeholder: "Select",
      required: true,
      customValidation: (rule) =>
        rule.test("valid-year-or-date", "Invalid date format", (value) => {
          if (!value) return true;
          return (
            moment(value, "YYYY", true).isValid() ||
            moment(value, "YYYY-MM-DD", true).isValid()
          );
        }),
    },

    {
      name: "marks_type",
      colSpan: 6,
      type: "select",
      label: "Marks Type",
      placeholder: "Enter here",
      options: [
        { id: 1, label: "Percentages", value: "percentage" },
        { id: 2, label: "CGPA", value: "cgpa" },
      ],
      required: true,
    },
    {
      name: "marks",
      colSpan: 6,
      type: "number",
      label: "Marks",
      placeholder: "Enter here",
      required: true,
      customValidation: (rule) =>
    rule.test(
      "valid-marks",
      "Please Input Value as per marks type - Percentage (1-100) CGPA-(1-10)",
      function (value) {
        const { marks_type } = this.parent; 

        if (!value) return true; 

        if (marks_type === "cgpa") {
          return value >= 1 && value <= 10;
        }
        if (marks_type === "percentage") {
          return value >= 1 && value <= 100;
        }

        return true;
      }
    ),
    },
    {
      name: "university",
      colSpan: 12,
      type: "text",
      label: "Institute / University",
      placeholder: "Enter here",
      required: true,
    },
    {
      name: "address",
      colSpan: 12,
      type: "text",
      label: "Address",
      placeholder: "Enter Address",
      required: true,
    },
    {
      name: "documents",
      colSpan: 12,
      type: "file",
      label: "Upload Files",
      placeholder: "Choose files",
      accept: ["pdf", "doc", "docx", "jpg", "png"],
      limit: 5,
      // folder: "user-documents",
    },
  ];

  // Map file objects to only path strings for API
  const documentsToPaths = (docs, isEdit = false) => {
    if (!Array.isArray(docs) || docs.length === 0) return [];
    return docs
      .map((doc) => {
        if (typeof doc === "string") return doc;
        const raw =
          // Support both prefilled cell objects ({ data }) and uploader file objects
          doc.data ||
          doc.path ||
          doc.uploadUrl ||
          doc?.serverResponse?.path ||
          doc?.serverResponse?.uploadUrl ||
          "";

        // On edit, ignore prefilled pseudo files that only have data/localpreview (existing image)
        if (
          isEdit &&
          raw &&
          !doc.path &&
          !doc.uploadUrl &&
          !doc?.serverResponse
        ) {
          return null;
        }

        return raw.replace(/^https?:\/\/[^/]+\/storage\/?/i, "");
      })
      .filter(Boolean);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const paths = documentsToPaths(values.documents, isEditMode);

    const payload = {
      user_id: staff.id,
      qualification: values.qualification,
      degree: values.degree,
      field_of_study: values.specialization,
      school: values.university,
      location: values.address,
      passing_year: moment(values.year_of_passing).format("YYYY"),
      marks_type: values.marks_type,
      marks: values.marks,
    };

    // Only send image when user actually selected/changed a file
    if (paths[0]) {
      payload.image = paths[0];
    }

    try {
      if (isEditMode && currentRecord?.id) {
        // Update existing record
        await handleUpdate(payload, currentRecord.id, updateEducation, {
          successMessage: "Education updated successfully!",
          errorMessage: "Failed to update education!",
        });
        heandleFetchData();
      } else {
        // Create new record
        await handleCreate(payload, createEducation, {
          successMessage: "Education created successfully!",
          errorMessage: "Failed to create education!",
        });
        heandleFetchData();
      }

      // 🔄 Refresh list after add/edit
      const updatedList = await getEducationList(educationListParams);
      setEducationData(Array.isArray(updatedList) ? updatedList : []); // keep education list in state
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    } finally {
      setSubmitting(false);

      // Reset state and form after a short delay
      setTimeout(() => {
        resetForm();
        setIsEditMode(false);
        setCurrentRecord(null);
        setIsOpenSheet(false);
      }, );
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setCurrentRecord(null);
    setIsOpenSheet(false);
  };

  return (
    <div className="flex flex-col bg-white rounded-md h-full">
      {loadingEducation ? (
        <div>
          <div className="border-b flex justify-between p-4">
            <h2 className="text-lg font-semibold text-blue-900">
              Education Details
            </h2>
          </div>
          <div className="p-4 flex items-center justify-center min-h-[120px]">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-theme" />
          </div>
        </div>
      ) : tableData.length > 0 ? (
        <div>
          <div className="border-b flex justify-between p-4">
            <h2 className="text-lg font-semibold text-blue-900">
              Education Details
            </h2>
            <DefaultButton
              name={Icons.ADD}
              onClick={() => setIsOpenSheet(true)}
            />
          </div>
          <div className="p-4">
            <CommonTable
              TableHeaderData={TableHeaderData}
              tabledata={tableData}
              loading={false}
              action={actionButton}
            />
          </div>
        </div>
      ) : (
        <NoData
          title="Not Found"
          message="No Education Details Found"
          buttonText="Add Education"
          onClick={() => setIsOpenSheet(true)}
        />
      )}

      <BottomDrawer
        classNames={"!w-[700px]"}
        title={isEditMode ? "Edit Education Details" : "Add Education"}
        isOpen={isOpenSheet}
        position="right"
        setIsOpen={handleCancel}
      >
        <CommonForm
          fields={userFormFields}
          initialData={currentRecord || { year_of_passing: "" }}
          onSubmit={handleSubmit}
          submitButtonText={isEditMode ? "Update" : "Submit"}
          onCancel={handleCancel}
          cancelButtonText="Cancel"
          columnsPerRow={2}
        />
      </BottomDrawer>
    </div>
  );
};

export default EducationCard;
